import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import '@Pages/GeoMMM/ScenarioPlanning/Modals/EditScenarioModal.scss';
import cn from 'classnames';
import * as R from 'ramda';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactElement,
  Dispatch,
  SetStateAction
} from 'react';
import { Modal } from 'react-bootstrap';
import {
  MdClose,
  MdError,
  MdOutlineArrowBackIos,
  MdOutlineArrowForwardIos,
  MdOutlineUploadFile,
  MdReplay
} from 'react-icons/md';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { AnalyzeScenarioResult } from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningLambdaUtils';
import { CsvUploadModal } from '@Pages/GeoMMM/ScenarioPlanning/Modals/CsvUpload/CsvUploadModal';
import { RunScenarioProgressDialog } from '@Pages/GeoMMM/ScenarioPlanning/Modals/RunScenarioProgressDialog';
import { ParsedUploadRow } from '@Pages/GeoMMM/ScenarioPlanning/Modals/CsvUpload/CsvUploadUtils';
import { FlightingSparkChart } from '@Components/ScenarioPlanningChart/FlightingSparkChart';
import {
  buildSparkChartData,
  getDisplayedDeltaPercent
} from '@Components/ScenarioPlanningChart/scenario-planning-chart-utils';
import { Button } from '@Components/Button';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';

import {
  analyzeScenario,
  applyFlightingChange,
  applySetTotalValue,
  capitalizeNames,
  getCostMetricLabel,
  getEditSortOptionLabel,
  getEffectLabel,
  getVisibleNodes,
  GROUP_BY_ENUM,
  GROUP_BY_OPTIONS,
  isNA,
  ModelConfig,
  ModelRevenueBreakdown,
  MODEL_TYPES,
  PairwiseInteraction,
  SCENARIO_METRIC_ENUM,
  ScenarioDateRow,
  ScenarioNode,
  scenarioRedshiftDataToTree,
  SORT_BY_ENUM,
  SORT_DIRECTION_ENUM,
  SORT_DIRECTION_LABEL_MAP,
  SORT_DIRECTION_OPTIONS,
  SORT_OPTIONS,
  stripDcDgMarker,
  sumWeightedTotalByModel,
  updateTreeWithAnalyzedData
} from '@Pages/GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { useSetError } from '@src/redux/modals';
import { Brand60 } from '@src/utils/colors';

const ROW_INDENTATION = 16;
const MAX_SPEND_FACTOR = 2;
const SPEND_ARROW_STEP = 1;
const PERCENT_ARROW_STEP = 1;

// ==== Which comparison column the panel currently shows — the settled BAU baseline,
// the last-Applied scenario ("Current Scenario" in the UI, despite the name — it's a snapshot,
// not a live value), or a client-side Live Preview estimate of the in-progress, pre-Apply edit. ====
const COMPARE_MODE = {
  BASELINE: 'baseline',
  APPLIED: 'applied',
  LIVE: 'live'
} as const;
type COMPARE_MODE = (typeof COMPARE_MODE)[keyof typeof COMPARE_MODE];

// Round to whole dollars (the input's display precision); the +0.99 absorbs the
// rounding so a boundary input isn't rejected as out-of-range.
const maxSpendThreshold = (baseline: number): number =>
  Math.round(MAX_SPEND_FACTOR * baseline) + 0.99;

const computeCursorPosition = (oldVal: string, cursorPos: number, newFormatted: string): number => {
  const digitsBeforeCursor = oldVal.slice(0, cursorPos).replace(/[^0-9-]/g, '').length;
  let newPos = 0;
  let digitsSeen = 0;
  for (let i = 0; i < newFormatted.length; i++) {
    if (/[0-9-]/.test(newFormatted[i])) {
      digitsSeen++;
    }
    if (digitsSeen === digitsBeforeCursor) {
      newPos = i + 1;
      break;
    }
  }
  return newPos;
};

// ==== HROAS: getPrev/getBaseline/getValue are all re-derived from previousByModel/baselineByModel/
// currentByModel when the model-selection bar state is passed in, so this comparison panel
// respects toggling/reweighting instantly instead of always showing the full
// unweighted picture. The by-model sum uses direct+indirect COMBINED (sumWeightedTotalByModel),
// not direct alone — a model's whole revenue can live entirely in one field (e.g. Brand Equity's
// is 100% indirect post-reclassification), so summing only direct would silently drop it. The
// non-HROAS raw-value fallback intentionally stays direct-only, matching this panel's
// longstanding (pre-HROAS) convention for rMMM/GeoMMM. getValue reads currentDirectRevenue, a
// client-side constant-ROAS ESTIMATE — not a real model output, since actual revenue
// impact is only known after a real analyze/Apply run. ====
const buildComparisonCols = (
  outcomeVariable?: string,
  includedModelIds?: Set<string>,
  normalizedModelWeights?: Record<string, number>
) => {
  const weightedRev = (byModel: ModelRevenueBreakdown | undefined, rawValue: number): number =>
    byModel && includedModelIds && normalizedModelWeights
      ? sumWeightedTotalByModel(byModel, includedModelIds, normalizedModelWeights)
      : rawValue;

  return [
    {
      header: 'Spend',
      getValue: (node: ScenarioNode) => node.directSpend,
      getPrev: (node: ScenarioNode) => node.previousDirectSpend,
      getBaseline: (node: ScenarioNode) => node.baselineDirectSpend,
      getTotalValue: (nodes: ScenarioNode[]) => nodes.reduce((sum, n) => sum + n.directSpend, 0),
      getTotalPrev: (nodes: ScenarioNode[]) =>
        nodes.reduce((sum, n) => sum + n.previousDirectSpend, 0),
      getTotalBaseline: (nodes: ScenarioNode[]) =>
        nodes.reduce((sum, n) => sum + n.baselineDirectSpend, 0),
      prefix: '$',
      decimals: 0
    },
    {
      header: getEffectLabel(outcomeVariable),
      getValue: (node: ScenarioNode) =>
        weightedRev(node.currentByModel, node.currentDirectRevenue ?? node.directRevenue),
      getPrev: (node: ScenarioNode) =>
        weightedRev(node.previousByModel, node.previousDirectRevenue),
      getBaseline: (node: ScenarioNode) =>
        weightedRev(node.baselineByModel, node.baselineDirectRevenue),
      getTotalValue: (nodes: ScenarioNode[]) =>
        nodes.reduce(
          (sum, n) =>
            sum + weightedRev(n.currentByModel, n.currentDirectRevenue ?? n.directRevenue),
          0
        ),
      getTotalPrev: (nodes: ScenarioNode[]) =>
        nodes.reduce((sum, n) => sum + weightedRev(n.previousByModel, n.previousDirectRevenue), 0),
      getTotalBaseline: (nodes: ScenarioNode[]) =>
        nodes.reduce((sum, n) => sum + weightedRev(n.baselineByModel, n.baselineDirectRevenue), 0),
      prefix: '$',
      decimals: 0
    },
    {
      header: getCostMetricLabel(outcomeVariable),
      getValue: (node: ScenarioNode) => {
        const currentRev = weightedRev(
          node.currentByModel,
          node.currentDirectRevenue ?? node.directRevenue
        );
        return node.directSpend ? currentRev / node.directSpend : 0;
      },
      getPrev: (node: ScenarioNode) => {
        const prevRev = weightedRev(node.previousByModel, node.previousDirectRevenue);
        return node.previousDirectSpend ? prevRev / node.previousDirectSpend : 0;
      },
      getBaseline: (node: ScenarioNode) => {
        const baselineRev = weightedRev(node.baselineByModel, node.baselineDirectRevenue);
        return node.baselineDirectSpend ? baselineRev / node.baselineDirectSpend : 0;
      },
      getTotalValue: (nodes: ScenarioNode[]) => {
        const totalSpend = nodes.reduce((sum, n) => sum + n.directSpend, 0);
        const totalRev = nodes.reduce(
          (sum, n) =>
            sum + weightedRev(n.currentByModel, n.currentDirectRevenue ?? n.directRevenue),
          0
        );
        return totalSpend ? totalRev / totalSpend : 0;
      },
      getTotalPrev: (nodes: ScenarioNode[]) => {
        const totalSpend = nodes.reduce((sum, n) => sum + n.previousDirectSpend, 0);
        const totalRev = nodes.reduce(
          (sum, n) => sum + weightedRev(n.previousByModel, n.previousDirectRevenue),
          0
        );
        return totalSpend ? totalRev / totalSpend : 0;
      },
      getTotalBaseline: (nodes: ScenarioNode[]) => {
        const totalSpend = nodes.reduce((sum, n) => sum + n.baselineDirectSpend, 0);
        const totalRev = nodes.reduce(
          (sum, n) => sum + weightedRev(n.baselineByModel, n.baselineDirectRevenue),
          0
        );
        return totalSpend ? totalRev / totalSpend : 0;
      },
      prefix: '',
      decimals: 2
    }
  ];
};

interface EditScenarioModalProps {
  data: ScenarioDateRow[];
  end: string;
  isFirstRun: boolean;
  modelConfig: ModelConfig;
  newScenarioTree: ScenarioNode[];
  onApply: (
    currentScenarioDataMap: Record<string, ScenarioDateRow>,
    pairwiseData?: PairwiseInteraction[],
    rawResult?: AnalyzeScenarioResult
  ) => void;
  onHide: () => void;
  outcomeVariable?: string;
  setNewScenarioTree: Dispatch<SetStateAction<ScenarioNode[] | undefined>>;
  start: string;
  // ==== HROAS: current model-selection-bar state, see buildComparisonCols above ====
  includedModelIds?: Set<string>;
  normalizedModelWeights?: Record<string, number>;
}

export const EditScenarioModal = (props: EditScenarioModalProps): ReactElement => {
  const {
    data,
    end,
    isFirstRun,
    modelConfig,
    newScenarioTree,
    outcomeVariable,
    onApply,
    onHide,
    setNewScenarioTree,
    start,
    includedModelIds,
    normalizedModelWeights
  } = props;
  // ==== HROAS: the Live Preview tab is HROAS-only — gate it and its fallback effect below so it
  // doesn't newly appear on single-model GeoMMM/rMMM/BrandEquity edits. ====
  const isHroas = modelConfig.modelType === MODEL_TYPES.HROAS;
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [groupBy, setGroupBy] = useState<GROUP_BY_ENUM>(GROUP_BY_ENUM.CHANNEL);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [analysisPercent, setAnalysisPercent] = useState<number | null>(null);
  const [progressDialogHidden, setProgressDialogHidden] = useState<boolean>(false);
  const [modalError, setModalError] = useState<string>('');
  const [showCsvUploadModal, setShowCsvUploadModal] = useState(false);
  const [compareMode, setCompareMode] = useState<COMPARE_MODE>(COMPARE_MODE.BASELINE);
  const [sortBy, setSortBy] = useState<SORT_BY_ENUM>(SORT_BY_ENUM.ALPHABETICALLY);
  const [sortDirection, setSortDirection] = useState<SORT_DIRECTION_ENUM>(
    SORT_DIRECTION_ENUM.LOW_TO_HIGH
  );
  const [editingState, setEditingState] = useState<{
    key: string;
    field: 'spend' | 'percent' | 'delta';
    rawValue: string;
  } | null>(null);
  const pendingCursor = useRef<{ el: HTMLInputElement; pos: number } | null>(null);
  useLayoutEffect(() => {
    if (pendingCursor.current) {
      const { el, pos } = pendingCursor.current;
      el.setSelectionRange(pos, pos);
      pendingCursor.current = null;
    }
  });
  const setError = useSetError();

  const initialScenarioTree = useMemo(() => scenarioRedshiftDataToTree(data), [data]);

  const visibleNodes = useMemo(
    () =>
      newScenarioTree &&
      getVisibleNodes({ nodes: newScenarioTree, expandedMap, sortOption: sortBy, sortDirection }),
    [expandedMap, newScenarioTree, sortBy, sortDirection]
  );

  const nodeFlightingDataMap = useMemo(() => {
    const result: Record<string, ScenarioDateRow[]> = {};
    for (const row of data) {
      if (Number.isNaN(row.directSpend) || Number.isNaN(row.directRevenue)) {
        continue;
      }
      const { isDemandCapture } = row;
      let effChannel = row.channel && !isNA(row.channel) ? row.channel : 'Unknown Channel';
      if (isDemandCapture === true) {
        effChannel = `${effChannel}::dc`;
      } else if (isDemandCapture === false) {
        effChannel = `${effChannel}::dg`;
      }
      const effPlatform = row.platform && !isNA(row.platform) ? row.platform : effChannel;
      const effTactic = row.tactic && !isNA(row.tactic) ? row.tactic : effPlatform;
      for (const key of [
        effChannel,
        `${effChannel}, ${effPlatform}`,
        `${effChannel}, ${effPlatform}, ${effTactic}`
      ]) {
        const rows = result[key] ?? (result[key] = []);
        rows.push(row);
      }
    }
    return result;
  }, [data]);

  const hasAnyChanges = useMemo(() => {
    if (R.isNil(newScenarioTree)) {
      return false;
    }
    const nodeHasChanges = (node: ScenarioNode): boolean => {
      if (
        node.baselineDirectSpend !== node.directSpend ||
        node.baselineUseHistoricalFlighting !== node.useHistoricalFlighting
      ) {
        return true;
      }
      return (node.children || []).some(nodeHasChanges);
    };
    return newScenarioTree.some(nodeHasChanges);
  }, [newScenarioTree]);

  // ==== The Live Preview tab only makes sense while an edit is pending — fall back to
  // Baseline (always available, unlike Applied which is hidden on the first run) once the user
  // reverts every field back to its baseline value. ====
  useEffect(() => {
    if (isHroas && !hasAnyChanges && compareMode === COMPARE_MODE.LIVE) {
      setCompareMode(COMPARE_MODE.BASELINE);
    }
  }, [isHroas, hasAnyChanges, compareMode]);

  useEffect(() => {
    if (R.isNil(newScenarioTree)) {
      setNewScenarioTree(initialScenarioTree);
    }
  }, [data, initialScenarioTree, newScenarioTree, setNewScenarioTree]);

  // First-match-wins (modal shows one error at a time); validate the whole tree
  // on every edit since one row's change can move another into/out of violation.
  // Average baseline spend of the funded (non-zero) nodes at each tree depth
  // (0 = channel, 1 = platform, 2 = tactic). Used as the increase cap for a node
  // whose baseline is $0, where the usual 2x-of-baseline cap would pin it at $0.
  const avgSpendByLevel = useMemo(() => {
    const sums: number[] = [];
    const counts: number[] = [];
    const walk = (nodes: ScenarioNode[], depth: number) => {
      for (const node of nodes) {
        if (node.baselineDirectSpend > 0) {
          sums[depth] = (sums[depth] ?? 0) + node.baselineDirectSpend;
          counts[depth] = (counts[depth] ?? 0) + 1;
        }
        if (node.children?.length) {
          walk(node.children, depth + 1);
        }
      }
    };
    walk(newScenarioTree ?? [], 0);
    return counts.map((count, depth) => (count ? sums[depth] / count : 0));
  }, [newScenarioTree]);

  const maxSpendForNode = useCallback(
    (node: ScenarioNode, level: number): number =>
      node.baselineDirectSpend === 0
        ? Math.round(avgSpendByLevel[level] ?? 0) + 0.99
        : maxSpendThreshold(node.baselineDirectSpend),
    [avgSpendByLevel]
  );

  const validateScenarioTree = useCallback(
    function validate(nodes: ScenarioNode[], level = 0): string {
      for (const node of nodes) {
        if (node.directSpend < 0) {
          return `Spend for ${stripDcDgMarker(node.name)} cannot be negative`;
        }
        if (node.directSpend > maxSpendForNode(node, level)) {
          return node.baselineDirectSpend === 0
            ? `Spend for ${stripDcDgMarker(node.name)} cannot exceed the average spend of $${formatNumber(
                Math.round(avgSpendByLevel[level] ?? 0),
                0
              )}`
            : `Spend for ${stripDcDgMarker(node.name)} cannot be increased by more than ${
                (MAX_SPEND_FACTOR - 1) * 100
              }% from its baseline`;
        }
        const childError = validate(node.children || [], level + 1);
        if (childError) {
          return childError;
        }
      }
      return '';
    },
    [maxSpendForNode, avgSpendByLevel]
  );

  const applySpendChange = useCallback(
    (key: string, _node: ScenarioNode, newSpend: number) => {
      const updatedTree = applySetTotalValue({
        tree: newScenarioTree,
        nodeKey: key,
        newTotal: newSpend
      });
      setNewScenarioTree(updatedTree);
      setModalError(validateScenarioTree(updatedTree));
    },
    [newScenarioTree, setNewScenarioTree, validateScenarioTree]
  );

  const getDisplayPercent = useCallback(
    (node: ScenarioNode): number =>
      node.baselineDirectSpend !== 0
        ? Math.round(
            (100 * (node.directSpend - node.baselineDirectSpend)) / node.baselineDirectSpend
          )
        : 0,
    []
  );

  const getDisplayDelta = useCallback(
    (node: ScenarioNode): number => node.directSpend - node.baselineDirectSpend,
    []
  );

  const toggleNodeFlighting = useCallback(
    (key: string, current: boolean) =>
      setNewScenarioTree(
        applyFlightingChange({ tree: newScenarioTree, nodeKey: key, val: !current })
      ),
    [newScenarioTree, setNewScenarioTree]
  );

  const hasNoDelta = useCallback(
    (node: ScenarioNode): boolean => {
      const flightingUnchanged =
        node.baselineUseHistoricalFlighting === node.useHistoricalFlighting;
      // A $0 baseline can't express change as a percent (always 0%), so compare spend directly.
      if (node.baselineDirectSpend === 0) {
        return node.directSpend === 0 && flightingUnchanged;
      }
      return getDisplayPercent(node) === 0 && flightingUnchanged;
    },
    [getDisplayPercent]
  );

  const analyze = useCallback(() => {
    (async () => {
      setIsAnalyzing(true);
      setAnalysisPercent(null);
      setProgressDialogHidden(false);
      try {
        const {
          dataMap: currentScenarioDataMap,
          pairwiseData: newPairwiseData,
          rawResult
        } = await analyzeScenario({
          end,
          modelConfig,
          newScenarioTree,
          onProgress: progress => setAnalysisPercent(progress.percent ?? null),
          start
        });
        setNewScenarioTree(updateTreeWithAnalyzedData(newScenarioTree, currentScenarioDataMap));
        onApply(currentScenarioDataMap, newPairwiseData, rawResult);
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: reportError.message,
          reportError
        });
      } finally {
        setIsAnalyzing(false);
      }
    })();
  }, [modelConfig, end, newScenarioTree, onApply, setError, setNewScenarioTree, start]);

  // Sum over top-level nodes only — a parent's directSpend/baselineDirectSpend is
  // already a rollup of its children, so summing visibleNodes (which can include an
  // expanded parent AND its children at once) would double-count those branches.
  const totals = useMemo(() => {
    const nodes = newScenarioTree ?? [];
    const totalBaseline = nodes.reduce((sum, node) => sum + node.baselineDirectSpend, 0);
    const totalCurrent = nodes.reduce((sum, node) => sum + node.directSpend, 0);
    const totalDeltaDollar = totalCurrent - totalBaseline;
    const totalDeltaPercent =
      totalBaseline !== 0 ? Math.round((100 * totalDeltaDollar) / totalBaseline) : 0;
    return { totalCurrent, totalDeltaDollar, totalDeltaPercent };
  }, [newScenarioTree]);

  const header = useMemo(
    () =>
      newScenarioTree && (
        <Modal.Header>
          <div className='title'>Scenario Spend Details</div>
          <div className='modal-actions'>
            <Dropdown
              overlayPlacement={Placement.RIGHT}

              stretch={StretchMode.FIT}
              label='Sort by'
              onChange={val => {
                if (SORT_OPTIONS.includes(val as SORT_BY_ENUM)) {
                  setSortBy(val as SORT_BY_ENUM);
                } else {
                  setSortDirection(val as SORT_DIRECTION_ENUM);
                }
              }}
              options={
                R.map(
                  option => ({
                    className:
                      option === SORT_DIRECTION_ENUM.HIGH_TO_LOW
                        ? 'dropdown-menu-item-border-top'
                        : '',
                    label:
                      SORT_DIRECTION_LABEL_MAP[option as SORT_DIRECTION_ENUM] ??
                      getEditSortOptionLabel(option as SORT_BY_ENUM, outcomeVariable),
                    value: option
                  }),
                  [...SORT_OPTIONS, ...SORT_DIRECTION_OPTIONS]
                ) as DropdownOption[]
              }
              size={Size.SM}
              value={`${sortBy} ${sortDirection}`}
            />
            <Dropdown
              overlayPlacement={Placement.RIGHT}

              stretch={StretchMode.FIT}
              label='Group by'
              onChange={val => {
                setGroupBy(val);
                const newExpandedMap = {};
                const granularityByGroup: Partial<Record<GROUP_BY_ENUM, number>> = {
                  [GROUP_BY_ENUM.CHANNEL]: 0,
                  [GROUP_BY_ENUM.PLATFORM]: 1
                };
                const granularity = granularityByGroup[val] ?? 2;
                const newNodes = getVisibleNodes({
                  nodes: newScenarioTree,
                  expandedMap,
                  sortOption: sortBy,
                  sortDirection,
                  defaultExpandDepth: granularity + 1
                });
                for (const node of newNodes) {
                  const isExpandable =
                    node.node.children &&
                    (node.node.children.length > 1 ||
                      (node.node.children.length === 1 &&
                        node.node.children[0].name !== node.node.name));
                  newExpandedMap[node.key] = isExpandable && node.level < granularity;
                }
                setExpandedMap(newExpandedMap);
              }}
              options={GROUP_BY_OPTIONS}
              size={Size.SM}
              value={groupBy}
            />
            <Button
              data-testid='sp-csv-upload-btn'
              size={Size.SM}
              design={Design.NEUTRAL}
              leadingElement={<MdOutlineUploadFile />}
              onClick={() => setShowCsvUploadModal(true)}
            />
            <Button
              size={Size.SM}
              design={Design.NEUTRAL}
              leadingElement={<MdClose />}
              onClick={() => onHide()}
              appearance={Appearance.EMPTY}
            />
          </div>
        </Modal.Header>
      ),
    [expandedMap, groupBy, newScenarioTree, onHide, outcomeVariable, sortBy, sortDirection]
  );
  const body = useMemo(() => {
    if (R.isNil(newScenarioTree)) {
      return undefined;
    }
    // Sum over top-level nodes only, matching `totals` above — a parent's rollup
    // fields would otherwise double-count against its own expanded children.
    const topLevelNodes = newScenarioTree ?? [];
    const comparisonColumns = buildComparisonCols(
      outcomeVariable,
      includedModelIds,
      normalizedModelWeights
    ).map(colDef => {
      const cells = visibleNodes.map(({ node }) => {
        const baselineValue = colDef.getBaseline(node);
        if (compareMode === COMPARE_MODE.BASELINE) {
          return {
            value: `${colDef.prefix}${formatNumber(baselineValue, colDef.decimals)}`
          };
        }
        const currentValue =
          compareMode === COMPARE_MODE.LIVE ? colDef.getValue(node) : colDef.getPrev(node);
        const deltaPercent = getDisplayedDeltaPercent(currentValue, baselineValue, colDef.prefix);
        return {
          value: `${colDef.prefix}${formatNumber(currentValue, colDef.decimals)}`,
          delta: `${deltaPercent > 0 ? '+' : ''}${deltaPercent.toFixed(2)}%`
        };
      });
      const totalBaselineValue = colDef.getTotalBaseline(topLevelNodes);
      let totalCell: { value: string; delta?: string };
      if (compareMode === COMPARE_MODE.BASELINE) {
        totalCell = {
          value: `${colDef.prefix}${formatNumber(totalBaselineValue, colDef.decimals)}`
        };
      } else {
        const totalCurrentValue =
          compareMode === COMPARE_MODE.LIVE
            ? colDef.getTotalValue(topLevelNodes)
            : colDef.getTotalPrev(topLevelNodes);
        const totalDeltaPercent = getDisplayedDeltaPercent(
          totalCurrentValue,
          totalBaselineValue,
          colDef.prefix
        );
        totalCell = {
          value: `${colDef.prefix}${formatNumber(totalCurrentValue, colDef.decimals)}`,
          delta: `${totalDeltaPercent > 0 ? '+' : ''}${totalDeltaPercent.toFixed(2)}%`
        };
      }
      return {
        header: colDef.header,
        cells,
        totalCell
      };
    });
    return (
      <Modal.Body>
        <div className='spend-containers'>
          <div className='spend-inputs'>
            <div className='title'>New Scenario Details</div>
            <div className='option-headers'>
              <div className='spend'>Spend</div>
              <div className='delta'>Δ%</div>
              <div className='delta-dollar'>Δ$</div>
              <div className='flighting'>Flighting</div>
            </div>
            <div className='options'>
              {R.map(
                ({ node, level, key }) => (
                  <div
                    key={key}
                    className={cn(node.type, 'option')}
                    style={{ paddingLeft: `${(level + 1) * ROW_INDENTATION}px` }}
                  >
                    <InteractiveDiv
                      className={cn(
                        'expand-icon',
                        expandedMap[key] ? 'expanded' : 'collapsed',
                        node.children &&
                          node.children.length > 0 &&
                          !node.isDemandCapture &&
                          (node.children[0].name !== node.name || node.children.length > 1)
                          ? ''
                          : 'hide'
                      )}
                      onClick={() => setExpandedMap(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      {expandedMap[key] ? <MdOutlineArrowBackIos /> : <MdOutlineArrowForwardIos />}
                    </InteractiveDiv>
                    <div
                      className='node-demand'
                      style={{ visibility: R.isNil(node.isDemandCapture) ? 'hidden' : 'visible' }}
                    >
                      {node.isDemandCapture ? 'dc' : 'dg'}
                    </div>
                    <div
                      className='node-name'
                      style={{ width: `calc(10vw - ${level * ROW_INDENTATION}px)` }}
                    >
                      {capitalizeNames(stripDcDgMarker(node.name))}
                    </div>
                    {node.isDemandCapture ? (
                      <div className='demand-capture-text'>
                        Demand Capture spend cannot be adjusted manually.
                      </div>
                    ) : (
                      <>
                        <input
                          className='totals'
                          onChange={e => {
                            const cursorPos = e.target.selectionStart || 0;
                            const oldVal = e.target.value;
                            const cleanVal = oldVal.replace(/[^0-9-]/g, '');
                            const parsed = parseInt(cleanVal, 10);
                            if (!Number.isNaN(parsed)) {
                              setEditingState(null);
                              // Only apply when the parsed value actually differs from what's
                              // already shown. Typing a stripped character (e.g. a letter) re-parses
                              // the same displayed number, which must not mark the row as changed.
                              if (parsed !== Math.round(node.directSpend)) {
                                applySpendChange(key, node, parsed);
                              }
                              const newFormatted = `$${formatNumber(parsed, 0)}`;
                              pendingCursor.current = {
                                el: e.target,
                                pos: computeCursorPosition(oldVal, cursorPos, newFormatted)
                              };
                            } else {
                              setEditingState({ key, field: 'spend', rawValue: cleanVal });
                              pendingCursor.current = { el: e.target, pos: cursorPos };
                            }
                          }}
                          onBlur={() => setEditingState(null)}
                          onKeyDown={e => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                              e.preventDefault();
                              const delta =
                                e.key === 'ArrowUp' ? SPEND_ARROW_STEP : -SPEND_ARROW_STEP;
                              const newSpend = node.directSpend + delta;
                              if (newSpend >= 0 && newSpend <= maxSpendForNode(node, level)) {
                                setEditingState(null);
                                applySpendChange(key, node, newSpend);
                              }
                            }
                          }}
                          type='text'
                          value={
                            editingState?.key === key && editingState.field === 'spend'
                              ? editingState.rawValue
                              : `$${formatNumber(node.directSpend, 0)}`
                          }
                        />
                        <input
                          className='percent'
                          onChange={e => {
                            const cursorPos = e.target.selectionStart || 0;
                            const oldVal = e.target.value;
                            const cleanVal = oldVal.replace(/[^0-9-]/g, '');
                            const parsed = parseInt(cleanVal, 10);
                            if (!Number.isNaN(parsed)) {
                              setEditingState(null);
                              const newSpend = Math.round(
                                node.baselineDirectSpend * (1 + parsed / 100)
                              );
                              // Skip a no-op re-parse of the already-shown percent (e.g. when a
                              // stripped character is typed) so the row isn't marked as changed.
                              if (parsed !== getDisplayPercent(node)) {
                                applySpendChange(key, node, newSpend);
                              }
                              const newFormatted = `${getDisplayPercent({
                                ...node,
                                directSpend: newSpend
                              })}%`;
                              pendingCursor.current = {
                                el: e.target,
                                pos: computeCursorPosition(oldVal, cursorPos, newFormatted)
                              };
                            } else {
                              setEditingState({ key, field: 'percent', rawValue: cleanVal });
                              pendingCursor.current = { el: e.target, pos: cursorPos };
                            }
                          }}
                          onBlur={() => setEditingState(null)}
                          onKeyDown={e => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                              e.preventDefault();
                              const currentPercent = getDisplayPercent(node);
                              const newPercent =
                                currentPercent +
                                (e.key === 'ArrowUp' ? PERCENT_ARROW_STEP : -PERCENT_ARROW_STEP);
                              const newSpend = Math.round(
                                node.baselineDirectSpend * (1 + newPercent / 100)
                              );
                              if (newSpend >= 0 && newSpend <= maxSpendForNode(node, level)) {
                                setEditingState(null);
                                applySpendChange(key, node, newSpend);
                              }
                            }
                          }}
                          type='text'
                          value={
                            editingState?.key === key && editingState.field === 'percent'
                              ? editingState.rawValue
                              : `${getDisplayPercent(node)}%`
                          }
                        />
                        <input
                          className='delta-dollar'
                          onChange={e => {
                            const cursorPos = e.target.selectionStart || 0;
                            const oldVal = e.target.value;
                            const cleanVal = oldVal.replace(/[^0-9-]/g, '');
                            const parsed = parseInt(cleanVal, 10);
                            if (!Number.isNaN(parsed)) {
                              setEditingState(null);
                              const newSpend = node.baselineDirectSpend + parsed;
                              // Only apply when the parsed delta actually differs from what's
                              // already shown, matching the no-op guard on the spend/percent inputs.
                              if (parsed !== Math.round(getDisplayDelta(node))) {
                                applySpendChange(key, node, newSpend);
                              }
                              const newFormatted = `${parsed >= 0 ? '+' : '-'}$${formatNumber(
                                Math.abs(parsed),
                                0
                              )}`;
                              pendingCursor.current = {
                                el: e.target,
                                pos: computeCursorPosition(oldVal, cursorPos, newFormatted)
                              };
                            } else {
                              setEditingState({ key, field: 'delta', rawValue: cleanVal });
                              pendingCursor.current = { el: e.target, pos: cursorPos };
                            }
                          }}
                          onBlur={() => setEditingState(null)}
                          onKeyDown={e => {
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                              e.preventDefault();
                              const delta =
                                e.key === 'ArrowUp' ? SPEND_ARROW_STEP : -SPEND_ARROW_STEP;
                              const newSpend = node.directSpend + delta;
                              if (newSpend >= 0 && newSpend <= maxSpendForNode(node, level)) {
                                setEditingState(null);
                                applySpendChange(key, node, newSpend);
                              }
                            }
                          }}
                          type='text'
                          value={
                            editingState?.key === key && editingState.field === 'delta'
                              ? editingState.rawValue
                              : `${getDisplayDelta(node) >= 0 ? '+' : '-'}$${formatNumber(
                                  Math.abs(getDisplayDelta(node)),
                                  0
                                )}`
                          }
                        />
                        <div className='flighting-cell'>
                          <div
                            className='flighting-spark-chart'
                            onClick={() => toggleNodeFlighting(key, node.useHistoricalFlighting)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleNodeFlighting(key, node.useHistoricalFlighting);
                              }
                            }}
                            role='button'
                            tabIndex={0}
                            title={`Switch to ${
                              node.useHistoricalFlighting ? 'even' : 'historical'
                            } flighting`}
                          >
                            {(() => {
                              const flightingRows = nodeFlightingDataMap[key];
                              return flightingRows && flightingRows.length > 1 ? (
                                <FlightingSparkChart
                                  color={Brand60}
                                  data={buildSparkChartData(
                                    flightingRows,
                                    SCENARIO_METRIC_ENUM.SPEND,
                                    node.useHistoricalFlighting
                                  )}
                                  filled
                                  useHistoricalFlighting={node.useHistoricalFlighting}
                                />
                              ) : null;
                            })()}
                          </div>
                          <div className='flighting-caption'>
                            {node.useHistoricalFlighting ? 'Historical' : 'Even'}
                          </div>
                        </div>
                        <Button
                          size={Size.LG}
                          className='revert-button'
                          design={Design.NEUTRAL}
                          disabled={hasNoDelta(node)}
                          leadingElement={<MdReplay />}
                          onClick={() => {
                            setNewScenarioTree(
                              applyFlightingChange({
                                tree: applySetTotalValue({
                                  tree: newScenarioTree,
                                  nodeKey: key,
                                  newTotal: node.baselineDirectSpend
                                }),
                                nodeKey: key,
                                val: node.baselineUseHistoricalFlighting
                              })
                            );
                          }}
                          appearance={Appearance.EMPTY}
                        />
                      </>
                    )}
                  </div>
                ),
                visibleNodes
              )}
            </div>
            <div className='option-totals'>
              <div className='totals-label'>Total</div>
              <div className='spend'>${formatNumber(totals.totalCurrent, 0)}</div>
              <div className='delta'>
                {`${totals.totalDeltaPercent > 0 ? '+' : ''}${totals.totalDeltaPercent}%`}
              </div>
              <div className='delta-dollar'>
                {`${totals.totalDeltaDollar >= 0 ? '+' : '-'}$${formatNumber(
                  Math.abs(totals.totalDeltaDollar),
                  0
                )}`}
              </div>
            </div>
          </div>
          <div className='comparison-container'>
            <div className='title-container'>
              <InteractiveDiv
                className={cn('tab', {
                  active: compareMode === COMPARE_MODE.BASELINE || isFirstRun
                })}
                onClick={() => setCompareMode(COMPARE_MODE.BASELINE)}
              >
                BAU Scenario
              </InteractiveDiv>
              {!isFirstRun && (
                <InteractiveDiv
                  className={cn('tab', { active: compareMode === COMPARE_MODE.APPLIED })}
                  onClick={() => setCompareMode(COMPARE_MODE.APPLIED)}
                >
                  Current Scenario
                </InteractiveDiv>
              )}
              {isHroas && hasAnyChanges && (
                <InteractiveDiv
                  className={cn('tab', { active: compareMode === COMPARE_MODE.LIVE })}
                  onClick={() => setCompareMode(COMPARE_MODE.LIVE)}
                  title='Estimated from your pending spend edits, assuming baseline ROAS holds constant — not a real model result. Apply to see the actual analyzed impact.'
                >
                  Live Preview (est.)
                </InteractiveDiv>
              )}
            </div>
            <div className='comparison-headers'>
              {comparisonColumns.map(column => (
                <div key={column.header} className='column'>
                  <div className='header'>{column.header}</div>
                </div>
              ))}
            </div>
            <div className='comparison-data'>
              {comparisonColumns.map(column => (
                <div key={column.header} className='column'>
                  {column.cells.map((cell, rowIndex) => (
                    <div key={rowIndex} className='cell-wrapper'>
                      <div className='value'>{cell.value}</div>
                      {compareMode !== COMPARE_MODE.BASELINE && (
                        <div className='delta'>({cell.delta})</div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className='comparison-totals'>
              {comparisonColumns.map(column => (
                <div key={column.header} className='column'>
                  <div className='cell-wrapper total'>
                    <div className='value'>{column.totalCell.value}</div>
                    {compareMode !== COMPARE_MODE.BASELINE && (
                      <div className='delta'>({column.totalCell.delta})</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {modalError && (
          <div className='error-container'>
            <div className='error-icon'>
              <MdError />
            </div>
            <div className='error-text'>Error: {modalError}</div>
          </div>
        )}
      </Modal.Body>
    );
  }, [
    applySpendChange,
    compareMode,
    editingState,
    expandedMap,
    getDisplayDelta,
    getDisplayPercent,
    hasAnyChanges,
    hasNoDelta,
    isFirstRun,
    isHroas,
    maxSpendForNode,
    modalError,
    newScenarioTree,
    nodeFlightingDataMap,
    outcomeVariable,
    setNewScenarioTree,
    toggleNodeFlighting,
    totals,
    visibleNodes,
    includedModelIds,
    normalizedModelWeights
  ]);
  const footer = useMemo(
    () => (
      <Modal.Footer>
        <div className='left'>
          <Button
            disabled={!hasAnyChanges}
            onClick={() => {
              setNewScenarioTree(initialScenarioTree);
              setModalError('');
            }}
            appearance={Appearance.EMPTY}
          >
            Revert Changes
          </Button>
        </div>
        <div className='right'>
          <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={() => onHide()}>
            Cancel
          </Button>
          <Button
            data-testid='sp-apply-btn'
            onClick={() => analyze()}
            disabled={modalError.length > 0 || isAnalyzing}
          >
            Apply
          </Button>
        </div>
      </Modal.Footer>
    ),
    [
      analyze,
      hasAnyChanges,
      initialScenarioTree,
      isAnalyzing,
      modalError,
      onHide,
      setNewScenarioTree
    ]
  );
  const showProgressDialog = isAnalyzing && !progressDialogHidden;
  return (
    <>
      {/* Hide (not unmount) the edit modal while the progress dialog is up so the dialog
          sits over the page with a single translucent overlay; "Back to Editing" brings
          the edit modal back while the run continues. */}
      <Modal
        show={!showProgressDialog}
        onHide={onHide}
        centered
        className='scenario-planning-edit-scenario-modal'
        size='lg'
      >
        {header}
        {body}
        {footer}
      </Modal>
      <RunScenarioProgressDialog
        show={showProgressDialog}
        percent={analysisPercent}
        onBackToEditing={() => setProgressDialogHidden(true)}
      />
      {showCsvUploadModal && (
        <CsvUploadModal
          newScenarioTree={newScenarioTree}
          onApply={(rows: ParsedUploadRow[]) => {
            let tree = newScenarioTree;
            for (const { key, newSpend } of rows) {
              tree = applySetTotalValue({ tree, nodeKey: key, newTotal: newSpend });
            }
            setNewScenarioTree(tree);
            setModalError(validateScenarioTree(tree));
            setShowCsvUploadModal(false);
          }}
          onHide={() => setShowCsvUploadModal(false)}
        />
      )}
    </>
  );
};
